import axios, { AxiosRequestConfig, AxiosInstance, AxiosResponse } from 'axios'
import * as qs from 'qs'
import { Loading } from '../loading'
import { useUser, UserApi } from 'fx-front-ucenter'

const pending = new Map() // 声明一个数组用于存储每个ajax请求的取消函数和ajax标识

const { getToken } = useUser()

export interface FxAxiosConfig extends AxiosRequestConfig {
  noLoadingUrls?: string[]
  arrayBufferUrls?: string[]
  formDataUrls?: string[]
  hideErrorMessageCodes?: string[]
  reloginCodes?: string[]
  removeRepeatedUrl?: boolean
  serverConfig?: Record<string, any>
  axiosConfig?: any[]
  headerConfig?: Map<string[], Record<string, any>>
}

export class FxAxios {
  service: AxiosInstance
  constructor(axiosConfig = { timeout: 100000 } as FxAxiosConfig) {
    if (axiosConfig.serverConfig && typeof axiosConfig.serverConfig === 'object') {
      if (axiosConfig.serverConfig.swanUrl) {
        const xhr = new XMLHttpRequest()
        xhr.open('get', axiosConfig.serverConfig.swanUrl, false)
        xhr.onreadystatechange = () => {
          if (xhr.status === 200 && xhr.readyState === 4) {
            const responseJson = JSON.parse(xhr.responseText)

            responseJson.LOGO && setFavicon(responseJson.LOGO)
            responseJson.TITLE && (document.title = responseJson.TITLE)

            Object.keys(responseJson).forEach(k => {
              if (['[', '{'].includes(responseJson[k][0]) && [']', '}'].includes(responseJson[k][responseJson[k].length - 1])) {
                try {
                  responseJson[k] = JSON.parse(responseJson[k])
                } catch (e) {
                  console.error('parse serverconfig error', e)
                }
              }
            })

            Object.assign(axiosConfig.serverConfig, responseJson)
            localStorage.setItem('$SERVER_CONFIG', JSON.stringify(responseJson))
          }
        }
        xhr.send()
      }
    }
    this.service = axios.create(axiosConfig)
    // 设置 post、put 默认 Content-Type
    this.service.defaults.headers.post['Content-Type'] = 'application/json;charset=UTF-8'
    this.service.defaults.headers.put['Content-Type'] = 'application/json;charset=UTF-8'

    const noLoadingString = (axiosConfig.noLoadingUrls || []).join('|').replace(/\//g, '\\/')
    this.service.interceptors.request.use(
      config => {
        if (!config.url?.match(noLoadingString)) {
          Loading.showFullScreenLoading()
        }
        setResponseTypeToBuffer(config, axiosConfig)
        setHeaderToFormData(config, axiosConfig)
        setHeaderConfig(config, axiosConfig)
        const url = [config.method, config.url, qs.stringify(config.params), qs.stringify(config.data)].join('&')
        config.cancelToken = config.cancelToken ||
          new axios.CancelToken(cancel => {
            if (!pending.has(url)) {
              // 如果 pending 中不存在当前请求，则添加进去
              pending.set(url, cancel)
            }
          })
        this.handleRequestConfig(config)
        return config
      },
      error => {
        Loading.tryHideFullScreenLoading()
        console.error(error) // for debug
        return Promise.reject(error)
      },
    )

    this.service.interceptors.response.use(
      response => {
        if (!response.config.url?.match(noLoadingString)) {
          Loading.tryHideFullScreenLoading()
        }
        // removeRepeatUrl(response.config, axiosConfig); // 在一个ajax响应后再执行一下取消操作，把已经完成的请求从pending中移除
        if (response.data instanceof ArrayBuffer) {
          return response
        }
        const responseCode = response.data.code + ''
        // 成功/登录异常不显示错误提示
        if ((axiosConfig.hideErrorMessageCodes || ['0', '200010000', '1A0101', '3A0311']).every(c => c !== responseCode)) {
          // 约定只要responseCode不等于0，就是异常状态
          Loading.showMessageTip(2, response.data.desc || '系统异常')
          console.warn(`响应信息:${response.data.desc}`)
        }
        if ((axiosConfig.reloginCodes || ['1A0101', '3A0311']).includes(responseCode)) {
          if (window.parent !== window) {
            window.parent.postMessage({ method: 'logout' }, '*')
            ;(window as any).$wujie?.props?.method?.logout?.()
          } else {
            this.handleTokenExpired(response, axiosConfig)
          }
        }
        return response
      },
      error => {
        console.error(error.response) // for debug
        Loading.tryHideFullScreenLoading()
        // 可以根据后端返回的状态码进行不同的操作
        if (error.response) {
          const errorCode = error.response.status
          switch (errorCode) {
            case 400:
              Loading.showMessageTip(5, '请求出现错误')
              break
            case 401:
              Loading.showMessageTip(2, 'Token过期，请重新登录')
              this.handleTokenExpired(error.response, axiosConfig)
              break
            case 403:
              Loading.showMessageTip(2, '权限认证失效，请重新登录')
              this.handleAuthFailed(error.response, axiosConfig)
              break
            case 404:
              Loading.showMessageTip(5, '网络请求不存在')
              break
            case 500:
              Loading.showMessageTip(5, '系统异常')
              break
            case 501:
              Loading.showMessageTip(5, '请求还没有被实现')
              break
            case 502:
              Loading.showMessageTip(5, '网关错误')
              break
            case 503:
              Loading.showMessageTip(5, '服务暂时不可用')
              break
            case 505:
              Loading.showMessageTip(5, '请求的 HTTP 版本不支持')
              break
            default:
              break
          }
        }
        return Promise.reject(error)
      },
    )
  }
  handleRequestConfig(config: AxiosRequestConfig) {
    const token = getToken()
    if (token) {
      if (config.headers) config.headers.authorization = token
    }
  }
  handleTokenExpired(_: AxiosResponse, axiosConfig: FxAxiosConfig) {
    const loginUrl = encodeURIComponent(`${window.location.origin}/${axiosConfig.serverConfig?.PROD_PREFIX || ''}`)
    const url = UserApi.getUserCenterUrl(loginUrl)

    window.setTimeout(() => {
      // 退出到总集登录页
      window.location.href = url
    }, 10)
  }
  handleAuthFailed(_: AxiosResponse, axiosConfig: FxAxiosConfig) {
    this.handleTokenExpired(_, axiosConfig)
  }
}

const setHeaderToFormData = (config: AxiosRequestConfig, axiosConfig: FxAxiosConfig) => {
  // 设置content-type特殊方式
  const formDataFlag = (axiosConfig.formDataUrls || []).some(
    (url: any) => config.url?.indexOf(url) !== -1,
  )
  if (formDataFlag) {
    if (config.headers) config.headers['Content-Type'] = 'multipart/form-data'
  }
}

const setHeaderConfig = (config: AxiosRequestConfig, axiosConfig: FxAxiosConfig) => {
  const headerConfig = axiosConfig?.headerConfig ?? new Map<string[], Record<string, any>>()
  for(const entry of headerConfig.entries()) {
    const flag = (entry[0] || []).some(url => config.url?.indexOf(url) !== -1)
    if (flag && entry[1]) {
      if (config.headers) Object.assign(config.headers, { ...entry[1] })
    }
  }
}

// 下载文件配置
const setResponseTypeToBuffer = (config: AxiosRequestConfig, axiosConfig: FxAxiosConfig) => {
  const flag = (axiosConfig.arrayBufferUrls || []).some(item => config.url?.indexOf(item) !== -1)
  if (flag) {
    config.responseType = 'arraybuffer'
  }
}

const setFavicon = (url: string) => {
  if (url) {
    const favicon = (document.querySelector('link[rel*=\'icon\']') || document.createElement('link')) as HTMLLinkElement
    favicon.type = 'image/x-icon'
    favicon.rel = 'shortcut icon'
    favicon.href = url
    document.getElementsByTagName('head')[0].appendChild(favicon)
  }
}

// const removeRepeatUrl = ever => {
//   for (const p in pending) {
//     if (pending[p].u === ever.url + '&' + ever.method) {
//       //当当前请求在数组中存在时执行函数体
//       pending[p].f() //执行取消操作
//       pending.splice(p, 1) //把这条记录从数组中移除
//     }
//   }
// }

let fxAxios: AxiosInstance

const getFxAxios = (config: {
  serverConfig: Record<string, any> | undefined
  arrayBufferUrls: string[]
  noLoadingUrls: string[]
  formDataUrls: string[]
  hideErrorMessageCodes?: string[]
  reloginCodes?: string[]
  headerConfig?: Map<string[], Record<string, any>>
}) => {
  if (!fxAxios) {
    fxAxios = new FxAxios(config).service
  }
  return fxAxios
}

export { getFxAxios }

